import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, IconButton, Typography, Stack, Alert
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import { IntegrationSystemViewModel, IntegrationSettingsViewModel } from "../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import ShopifyOrderErrors from "../../components/Integration/ShopifyOrderErrors";
import GenericOrderErrors from "../../components/Integration/GenericOrderErrors";
import {
  LocationAutoComplete,
  ShopAutoComplete
} from "../../components/AutoComplete/AutoCompletes";

import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon, Close as CancelIcon, AddCard as AddCardIcon } from "@mui/icons-material";
import ActionMenu, { ActionItem } from "../../components/Card/ActionMenu";
import { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";

type BooleanIntegrationKeys = {
  [K in keyof IntegrationSystemViewModel]:
  IntegrationSystemViewModel[K] extends boolean | null | undefined ? K : never;
}[keyof IntegrationSystemViewModel];

const SUBSCRIBER_SETTINGS = [
  { field: "customer", labelKey: "columns.ext_Customer", descriptionKey: "integration.bool_customer" },
  { field: "salesOrder", labelKey: "columns.ext_SalesOrder", descriptionKey: "integration.bool_salesorder" },
  { field: "salesOrderCancellation", labelKey: "columns.ext_SalesOrderCancellation", descriptionKey: "integration.bool_cancel" },
  { field: "salesOrderInspection", labelKey: "columns.ext_SalesOrderInspection", descriptionKey: "integration.bool_inspection" },
  { field: "legacyInvoiceBilled", labelKey: "columns.ext_LegacyInvoiceBilled", descriptionKey: "integration.bool_invoice" },
  { field: "lockOrder", labelKey: "columns.ext_LockOrder", descriptionKey: "integration.bool_lockorder" },
  { field: "lockSalesLines", labelKey: "columns.ext_LockSalesLines", descriptionKey: "integration.bool_locklines" },
  { field: "salesReturnIntegrated", labelKey: "columns.ext_SalesReturnIntegrated", descriptionKey: "integration.bool_return" },
  { field: "salesReturnCreated", labelKey: "columns.ext_SalesReturnCreated", descriptionKey: "integration.bool_returncreated" },
  { field: "salesReturnPostedTrx", labelKey: "columns.ext_SalesReturnPostedTrx", descriptionKey: "integration.bool_postedreturn" },
  { field: "item", labelKey: "columns.ext_Item", descriptionKey: "integration.bool_item" },
  { field: "integrateItems", labelKey: "columns.ext_IntegrateItems", descriptionKey: "integration.bool_item" },
  { field: "purchaseOrder", labelKey: "columns.ext_PurchaseOrder", descriptionKey: "integration.bool_purchaseorder" },
  { field: "purchaseOrderIntegrated", labelKey: "columns.ext_PurchaseOrderIntegrated", descriptionKey: "integration.bool_purchaseManual" },
  { field: "purchaseOrderReceivedTrx", labelKey: "columns.ext_PurchaseOrderReceivedTrx", descriptionKey: "integration.bool_receivepurchase" },
  { field: "purchaseReturn", labelKey: "columns.ext_PurchaseReturn", descriptionKey: "integration.bool_purchasereturn" },
  { field: "inventory", labelKey: "columns.ext_Inventory", descriptionKey: "integration.bool_Inventory" },
  { field: "shipment", labelKey: "columns.ext_Shipment", descriptionKey: "integration.bool_shipment" },
  { field: "shipmentTrackingUpdate", labelKey: "columns.ext_ShipmentTrackingUpdate", descriptionKey: "integration.bool_tracking" },
  { field: "salesOrderPickCompleted", labelKey: "columns.ext_SalesOrderPickCompleted", descriptionKey: "integration.bool_salesorderpickcomplete" },
  { field: "salesOrderPackCompleted", labelKey: "columns.ext_SalesOrderPackCompleted", descriptionKey: "integration.bool_salesorderpackcomplete" },
  { field: "internalOrderPosted", labelKey: "columns.ext_InternalOrderPosted", descriptionKey: "integration.bool_postedinternalorders" },
  { field: "internalOrderIntegrated", labelKey: "columns.ext_InternalOrderIntegrated", descriptionKey: "integration.bool_integratedinternalorders" },
  { field: "shelfMove", labelKey: "columns.ext_ShelfMove", descriptionKey: "integration.bool_shelfmove" },
  { field: "warehouseLoadingCreated", labelKey: "columns.ext_WarehouseLoadingCreated", descriptionKey: "integration.bool_warehouseloadingorder" },
] as const satisfies ReadonlyArray<{
  field: BooleanIntegrationKeys;
  labelKey: string;
  descriptionKey: string;
}>;

const IntegrationEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { id = 0 } = params || {};
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading, isLoading } = useLoading();
  const { goBack, navigate } = useNavigation();
  const { t } = useTranslation();
  const [integration, setIntegration] = useState<IntegrationSystemViewModel & { originalValues?: Partial<IntegrationSystemViewModel>; }>({
    id: id,
    description: "",
    active: false,
    createNew: true
  });
  const [settings, setSettings] = useState<IntegrationSettingsViewModel[]>([]);
  const [settingDialogOpen, setSettingDialogOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<IntegrationSettingsViewModel | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const propertyTypes = ["", "String", "Bool", "Int32"];
  const [showAdvancedUrls, setShowAdvancedUrls] = useState(false);
  const [showIntegrationSubscriberSettings, setShowIntegrationSubscriberSettings] = useState(false);
  const [showOrderErrorDialog, setShowOrderErrorDialog] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

  const integrationTag = integration.tag?.toLowerCase();
  const isFortnox = integrationTag === "fortnox";
  const isShopify = integrationTag === "shopify";
  const isStarweb = integrationTag === "starweb";

  const showErrorButton = integration.id && (isShopify || isStarweb);
  useEffect(() => {
    const load = async () => {
      if (!id) return;
      showLoading();
      try {
        const res = await authService.get<IntegrationSystemViewModel>(`/integrationApi/GetIntegration?id=${id}`);
        setIntegration({ ...res, originalValues: { ...res }, hasChanges: false });
        setSettings(res.settings ?? []);
      } finally {
        hideLoading();
      }
    };
    load();
  }, [id]);

  const updateIntegrationField = <K extends BooleanIntegrationKeys>(
    field: K,
    value: boolean
  ) => {
    setIntegration(prev => ({
      ...prev,
      [String(field)]: value,
      hasChanges: true
    }));
  };


  const handleSave = async () => {
    showLoading();
    try {
      const res = await authService.post<IntegrationSystemViewModel>("/integrationApi/SaveIntegration", integration);
      setIntegration({ ...res, originalValues: { ...res }, hasChanges: false });
      showSnackbar(t("dialog.savedSuccessfully"), "success");
    } catch (error: any) {
      showSnackbar(`${t("dialog.saveErrorGeneric")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      hideLoading();
    }
  };

  const handleAddSetting = () => {
    setEditingSetting({ integrationId: integration.id, key: "", value: "", propertyType: "", fieldDescription: "" });
    setEditingIndex(null);
    setSettingDialogOpen(true);
  };

  const handleEditSetting = (setting: IntegrationSettingsViewModel, index: number) => {
    setEditingSetting({ ...setting });
    setEditingIndex(index);
    setSettingDialogOpen(true);
  };

  const handleSettingFieldChange = (field: keyof IntegrationSettingsViewModel, value: any) => {
    if (!editingSetting) return;
    setEditingSetting({ ...editingSetting, [field]: value });
  };

  const handleSaveSetting = async () => {
    if (!editingSetting) return;
    try {
      showLoading();
      const payload = { ...editingSetting, integrationId: integration.id };
      const res = await authService.post<IntegrationSettingsViewModel>("/integrationApi/SaveIntegrationSetting", payload);
      const updated = [...settings];
      if (editingIndex !== null && editingIndex >= 0) {
        updated[editingIndex] = res;
      } else {
        updated.push(res);
      }
      setSettings(updated);
      showSnackbar(t("dialog.savedSuccessfully"), "success");
      setSettingDialogOpen(false);
      setEditingSetting(null);
      setEditingIndex(null);
    } catch (error: any) {
      showSnackbar(`${t("dialog.saveErrorGeneric")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      hideLoading();
    }
  };


  const handleCancelChanges = () => {
    setIntegration((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as IntegrationSettingsViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const handleCloseSettingDialog = () => {
    setSettingDialogOpen(false);
    setEditingSetting(null);
    setEditingIndex(null);
  };

  const handleDeleteSetting = async (setting: IntegrationSettingsViewModel, index: number) => {
    const isConfirmed = window.confirm(t("dialog.areYouSure"));
    if (!isConfirmed) return;
    try {
      if (setting.id) {
        await authService.post("/integrationApi/DeleteIntegrationSetting", { id: setting.id });
      }
      const updated = [...settings];
      updated.splice(index, 1);
      setSettings(updated);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
    }
  };

  const truncateField = (str?: string | null, max = 15): string =>
    !str ? "" : (str.length > max ? str.slice(0, max) + "..." : str);

  //fortnox specific checks

  const reauthNeeded = settings?.some(
    x => x.key === "needsReauth" && x.value?.toLowerCase() === "true"
  );

  const fortNoxAccountInactive = isFortnox && settings?.some(
    x => x.key === "accountInactive" && x.value?.toLowerCase() === "true"
  );
  const fortNoxAccountMissing = isFortnox && settings?.some(
    x => x.key === "accountMissing" && x.value?.toLowerCase() === "true"
  );

  const handleDelete = async () => {
    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    if (integration.createNew == true) {

      navigate({ path: "/integration/list" });
      return;
    }
    showLoading();
    try {
      await authService.post(`/integrationApi/DeleteIntegration`, integration);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
      hideLoading();
      navigate({ path: "/integration/list" });

    } catch (error: any) {
      showSnackbar(`${t("dialog.failedDelete")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      hideLoading();
    }

  }
  //end fortnox specific checks
  return (
    <>
      <CenteredCard title={t("common.integration")}
        isLoading={isLoading}
        ref={toolbarRef}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={integration?.hasChanges ?? false}
      >

        <Grid container spacing={2}>
          <ActionMenu actions={[
            { title: t("common.delete"), onClick: handleDelete, color: "error" }
          ]}
          />
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("common.description")}
              InputLabelProps={{ shrink: true }}
              value={integration.description}
              onChange={(e) => setIntegration({ ...integration, description: e.target.value, hasChanges: true })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("common.tag")}
              InputLabelProps={{ shrink: true }}
              disabled={integration.id != null && integration.id != undefined && integration.id != 0}
              value={integration.tag || ""}
              onChange={(e) => setIntegration({ ...integration, tag: e.target.value, hasChanges: true })}
            />
          </Grid>
          {isFortnox && (
            <>
              {reauthNeeded && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {t("dialog.fortNoxReAuthError")}
                  </Alert>
                </Grid>
              )}

              {fortNoxAccountInactive && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {t("dialog.fortNoxAccountInactive")}
                  </Alert>
                </Grid>
              )}


              {fortNoxAccountMissing && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    {t("dialog.fortNoxAccountMissing")}
                  </Alert>
                </Grid>
              )}


              <Grid item xs={12}>
                <Button
                  variant={"contained"}
                  color={reauthNeeded ? "error" : "success"}
                  onClick={() =>
                    navigate({
                      path: "/integration/fortnox/setup",
                      params: { integrationId: integration.id },
                    })
                  }
                >
                  {reauthNeeded ? t("common.reInstall") : t("common.install")}
                </Button>
              </Grid>
            </>
          )}
          {showErrorButton == true && integration.id && (
            <Grid item xs={12}>
              <Button variant="outlined" onClick={() => setShowOrderErrorDialog(true)}>
                {t("common.showErrors")}
              </Button>

              <Dialog
                open={showOrderErrorDialog}
                onClose={() => setShowOrderErrorDialog(false)}
                maxWidth="md"
                fullWidth
              >

                <DialogContent dividers>
                  {isShopify && (
                    <ShopifyOrderErrors integrationId={integration.id} />
                  )}

                  {isStarweb && (
                    <GenericOrderErrors integrationId={integration.id} />
                  )}

                </DialogContent>

                <DialogActions>
                  <Button onClick={() => setShowOrderErrorDialog(false)}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>

          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={integration.active === true}
                  onChange={(e) => setIntegration({ ...integration, active: e.target.checked })}
                />
              }
              label={t("common.active")}
            />
          </Grid>

          <Grid item xs={12}>

            <FormControl fullWidth margin="normal">
              <LocationAutoComplete
                key={integration.locationCodeFilter || ""}
                valueId={integration.locationCodeFilter || ""}
                onChange={(selected) => setIntegration({ ...integration, locationCodeFilter: selected?.value || "", hasChanges: true })}

                infoText={t("help.locationHelp")}
              />
            </FormControl>

          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth margin="normal">
              <ShopAutoComplete
                key={integration.shopNo || ""}
                valueId={integration.shopNo || ""}
                onChange={(selected) => setIntegration({ ...integration, shopNo: selected?.value || "", hasChanges: true })}

                infoText={t("help.shopHelp")}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Order Prefix"
              value={integration.orderPrefix || ""}
              onChange={(e) => setIntegration({ ...integration, orderPrefix: e.target.value, hasChanges: true })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Integration Url"
              value={integration.serviceUrl1 || ""}
              onChange={(e) => setIntegration({ ...integration, serviceUrl1: e.target.value, hasChanges: true })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={showAdvancedUrls}
                  onChange={(e) => setShowAdvancedUrls(e.target.checked)}
                />
              }
              label={t("common.advancedUrls")}
            />
          </Grid>
          {showAdvancedUrls && (
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  padding: 2,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Service Url 2"
                      InputLabelProps={{ shrink: true }}
                      value={integration.serviceUrl2 || ""}
                      onChange={(e) => setIntegration({ ...integration, serviceUrl2: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Service Url 3"
                      InputLabelProps={{ shrink: true }}
                      value={integration.serviceUrl3 || ""}
                      onChange={(e) => setIntegration({ ...integration, serviceUrl3: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Service Url 4"
                      InputLabelProps={{ shrink: true }}
                      value={integration.serviceUrl4 || ""}
                      onChange={(e) => setIntegration({ ...integration, serviceUrl4: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={integration.debug === true}
                  onChange={(e) => setIntegration({ ...integration, debug: e.target.checked })}
                />
              }
              label="Debug"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={showIntegrationSubscriberSettings}
                  onChange={(e) => setShowIntegrationSubscriberSettings(e.target.checked)}
                />
              }
              label={t("common.subScriberSettings")}
            />
          </Grid>
          {showIntegrationSubscriberSettings && (
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "background.paper",
                  padding: 2,
                  borderRadius: 2,
                  boxShadow: 2,
                }}
              >
                <Grid container spacing={2}>
                  {SUBSCRIBER_SETTINGS.map(({ field, labelKey, descriptionKey }) => (
                    <Grid item xs={12} key={String(field)}>
                      <Alert severity="info" sx={{ mb: 1 }}>
                        {t(descriptionKey)}
                      </Alert>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={integration?.[field] === true}
                            onChange={(e) => updateIntegrationField(field, e.target.checked)}
                          />
                        }
                        label={t(labelKey)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          )}
          {!integration.createNew && (
            <>
              <Grid item xs={12}>
                <Button variant="outlined" onClick={handleAddSetting} sx={{ mb: 1 }}>
                  {t("common.add")} {t("common.settings")}
                </Button>
              </Grid>
              {settings.map((s, idx) => (
                <Grid
                  item
                  xs={12}
                  sm={settings.length === 1 ? 12 : settings.length === 2 ? 6 : 6}
                  md={settings.length === 1 ? 12 : settings.length === 2 ? 6 : 4}
                  key={s.key ?? idx}
                >
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      p: 1,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      cursor: "pointer",
                      ":hover": { backgroundColor: "#f0f0f0" },
                    }}
                  >
                    <Grid item xs={10} sx={{ wordBreak: "break-word" }}>
                      <Typography variant="body2" sx={{ color: "gray" }}>
                        <strong>{t("common.key")}:</strong>   {truncateField(s.key)}
                      </Typography>

                      <Typography variant="body1" sx={{ mt: 1 }}>
                        <strong>  {t("common.value")}:</strong> {truncateField(s.value)}
                      </Typography>

                      {s.fieldDescription && (
                        <Typography variant="body1">
                          <strong> {t("common.description")}:</strong> {truncateField(s.fieldDescription)}
                        </Typography>
                      )}

                      {s.propertyType && (
                        <Typography variant="body1">
                          <strong>Property Type:</strong> {truncateField(s.propertyType)}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={2}>
                      <Stack spacing={1} direction="row" justifyContent="flex-end">
                        <Tooltip title={t("common.edit")}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditSetting(s, idx)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSetting(s, idx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </>
          )}

        </Grid>
        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>
      <Dialog open={settingDialogOpen} onClose={handleCloseSettingDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t("common.settings")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Key"
            disabled={editingSetting?.id != null}
            value={editingSetting?.key || ""}
            onChange={(e) => handleSettingFieldChange("key", e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("common.value")}
            value={editingSetting?.value || ""}
            onChange={(e) => handleSettingFieldChange("value", e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("common.description")}
            value={editingSetting?.fieldDescription || ""}
            onChange={(e) => handleSettingFieldChange("fieldDescription", e.target.value)}
          />
          <TextField
            select
            fullWidth
            margin="dense"
            label="Property Type"
            value={editingSetting?.propertyType || ""}
            onChange={(e) => handleSettingFieldChange("propertyType", e.target.value)}
          >
            {propertyTypes.map((pt) => (
              <MenuItem key={pt} value={pt}>
                {pt}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettingDialog}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleSaveSetting}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IntegrationEditPage;
